// The library detail's in-place material editor (the only material editor:
// the material tab is retired, library layout 2026-10-01), and the package
// tab's centre (split out of Centre.tsx).

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { EditableMaterial } from "../../../components/EditorHost/EditableMaterial.js";
import { ExportView } from "../../../components/ExportView/ExportView.js";
import { MaterialHeader } from "../../../components/MaterialPreview/MaterialPreview.js";
import { friendlySourceTitles } from "../../../components/MaterialPreview/materialPreviewModel.js";
import { MaterialToolbar } from "../../../components/MaterialToolbar/MaterialToolbar.js";
import { projectModuleParts } from "../../../components/ModuleParts/ModuleParts.js";
import type {
    ExportRef,
    FocusTarget,
} from "../../../components/Navigation/types.js";
import { RowOverflow } from "../../../components/RowOverflow/RowOverflow.js";
import { useAppShell } from "../context.js";
import { PaneError, spinnerSlot } from "./slots.js";

// The editor-scoped contextual toolbar (chrome-relocation): the edited
// material's verbs — insert / undo-redo / export. The lifecycle menu lives on
// the library row.
const EditorToolbar = (): ReactNode => {
    const { editor, openOverlay, activeMaterialId } = useAppShell();
    return (
        <MaterialToolbar
            onInsert={() => openOverlay({ type: "insert" })}
            canUndo={editor.history.past.length > 0}
            canRedo={editor.history.future.length > 0}
            onUndo={editor.undo}
            onRedo={editor.redo}
            onExport={() => {
                if (activeMaterialId !== null)
                    openOverlay({
                        type: "export",
                        materialId: activeMaterialId,
                    });
            }}
        />
    );
};

// The edited material's document, wired to the editor host (the shared
// EditableMaterial face), or its load error / spinner. `onOpenPart`: where a
// part opens (default: this same Library detail).
export const EditedDocument = ({
    target,
    onOpenPart,
}: {
    readonly target: FocusTarget | undefined;
    readonly onOpenPart?: (id: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const {
        editor,
        summonSlot,
        openOverlay,
        library,
        activeMaterialId,
        dispatch,
    } = useAppShell();
    if (editor.materialError !== null)
        return <PaneError error={editor.materialError} />;
    if (editor.elements === null) return spinnerSlot(t`Loading material…`);
    return (
        <EditableMaterial
            elements={editor.elements}
            editor={editor}
            onInsert={() => openOverlay({ type: "insert" })}
            // No source doc to scroll to — reveal the grounding pane.
            onJumpToSource={() => summonSlot("grounding")}
            target={target}
            parts={projectModuleParts(library, activeMaterialId, t`Untitled`)}
            // A part opens in this same Library detail.
            onOpenPart={
                onOpenPart ??
                ((id) => dispatch({ type: "open-search", selectedId: id }))
            }
        />
    );
};

// A material in the LIBRARY detail, edited in place (library layout
// 2026-10-01): the same header as a source (title, the editor's actions
// beside it, a quiet facts line), then the editable document. The editor
// host follows the library's selected material (`editedMaterialRef`).
export const LibraryMaterial = ({
    title,
    target,
}: {
    readonly title: string;
    readonly target?: FocusTarget;
}): ReactNode => {
    const { editor, openRef, library } = useAppShell();
    return (
        <article className="bilda-material-preview">
            {editor.materialProvenance !== null ? (
                <MaterialHeader
                    title={title}
                    provenance={editor.materialProvenance}
                    // A source chip selects that source in this library.
                    onOpenSource={openRef}
                    friendlyTitles={friendlySourceTitles(
                        library?.sources ?? [],
                    )}
                    action={<EditorToolbar />}
                />
            ) : null}
            <EditedDocument target={target} />
        </article>
    );
};

// The package tab's centre (ExportView). A package is the only resource that
// still opens as a tab.
export const ExportCentre = ({
    exportRef,
}: {
    exportRef: ExportRef;
}): ReactNode => {
    const { t } = useLingui();
    const {
        ports,
        exportError,
        exportDetail,
        showNotice,
        resourceOverflowItems,
    } = useAppShell();
    if (exportError !== null) return <PaneError error={exportError} />;
    if (exportDetail === null) return spinnerSlot(t`Loading package…`);
    return (
        <ExportView
            title={exportRef.title}
            contents={exportDetail.contents}
            status={exportDetail.status}
            onDownload={() => {
                const url = exportDetail.downloadUrl;
                if (url !== undefined) {
                    ports.downloadExport(url);
                    showNotice({
                        type: "exported",
                        filename: exportRef.title,
                    });
                }
            }}
            overflow={
                <RowOverflow
                    label={t`Actions for ${exportRef.title}`}
                    items={resourceOverflowItems(exportRef)}
                />
            }
        />
    );
};
